import { fieldToday } from './field-overview';

import type { FieldDestination } from './field-overview';
import type { FieldActorId, FieldSnapshot } from '@mio/shared';

export interface FieldNextStep {
  title: string;
  reason: string;
  label: string;
  destination: FieldDestination;
}

export function fieldNextStep(
  snapshot: FieldSnapshot,
  unreadByActor: Partial<Record<FieldActorId, number>>,
): FieldNextStep {
  const discovery = snapshot.discovery;
  if (!discovery) return legacyNextStep(snapshot, unreadByActor);
  const work = fieldToday(snapshot);
  const unresolved = discovery.decisions.filter(
    (item) => item.status === 'open' || item.status === 'proposed',
  );
  const findings = discovery.findings.filter(
    (item) => item.status !== 'resolved' && item.status !== 'deferred',
  );
  const firstFinding = findings[0];
  const firstDecision = unresolved.find((item) => item.impact === 'high') ?? unresolved[0];
  const firstTicket =
    work.activeTickets.find((item) => item.status === 'in_progress') ?? work.activeTickets[0];
  const incident = work.openIncidents[0];
  const candidate =
    snapshot.prs.find((item) => item.id === snapshot.release.prId) ??
    [...snapshot.prs].sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))[0];
  const attention =
    work.reviewRequests[0] ??
    snapshot.prs.find((pr) =>
      snapshot.actors.some(
        (actor) =>
          pr.reviews
            .filter((review) => review.actorId === actor.id && review.revision === pr.revision)
            .at(-1)?.status === 'failed',
      ),
    );
  const pendingReview = snapshot.prs.find((pr) =>
    pr.reviews.some((review) => review.revision === pr.revision && review.status === 'pending'),
  );
  const newFeedback = snapshot.prs.find((pr) =>
    pr.reviews.some(
      (review) =>
        review.revision === pr.revision &&
        review.status !== 'pending' &&
        review.status !== 'stale' &&
        unreadByActor[review.actorId],
    ),
  );
  const reviewToRead = attention ?? newFeedback;
  const unreadActor = snapshot.actors.find((actor) => unreadByActor[actor.id]);
  if (snapshot.engagement.status === 'active' && !incident && reviewToRead)
    return {
      title: attention ? 'Your review needs attention' : 'Your colleagues have reviewed your work',
      reason: `${reviewToRead.title} · Read what works, what needs changing, and what remains unverified.`,
      label: 'Read review feedback',
      destination: { section: 'reviews', focusId: reviewToRead.id },
    };
  if (snapshot.engagement.status === 'active' && !incident && !firstFinding && pendingReview)
    return {
      title: 'Your colleagues are reviewing your submission',
      reason: 'You can keep building while checks and reviews run. Feedback arrives automatically.',
      label: 'View review progress',
      destination: { section: 'reviews', focusId: pendingReview.id },
    };
  if (
    snapshot.engagement.status === 'active' &&
    !incident &&
    !firstFinding &&
    unreadActor &&
    (candidate || discovery.decisions.length)
  )
    return {
      title: `New feedback from ${unreadActor.name.split(' ')[0]}`,
      reason: 'Continue the conversation and clarify the next useful change.',
      label: 'Read conversation',
      destination: { section: 'inbox', actorId: unreadActor.id },
    };
  return snapshot.engagement.status !== 'active'
    ? {
        title: 'Review the delivery evidence',
        reason: 'Your work, decisions, and assessment remain available.',
        label: 'View assessment',
        destination: { section: 'assessment' },
      }
    : incident
      ? {
          title: incident.title,
          reason: 'An operational issue needs attention.',
          label: 'Review incident',
          destination: { section: 'operations', focusId: incident.id },
        }
      : firstFinding
        ? {
            title: firstFinding.title,
            reason: 'Review the evidence and agree the appropriate follow-up.',
            label: 'Review finding',
            destination: { section: 'engagement', focusId: firstFinding.id },
          }
        : !discovery.decisions.length
          ? {
              title: 'Understand the problem before shaping the work',
              reason: discovery.brief.summary,
              label: 'Read the project brief',
              destination: { section: 'engagement', focusId: discovery.brief.id },
            }
          : firstDecision
            ? {
                title: firstDecision.title,
                reason:
                  firstDecision.status === 'proposed'
                    ? 'The owner’s confirmation is pending. You can continue independent work while they review.'
                    : 'Clarify the impact, record the evidence, and ask the relevant owner to confirm.',
                label: 'Review understanding',
                destination: { section: 'engagement', focusId: firstDecision.id },
              }
            : firstTicket
              ? {
                  title: firstTicket.title,
                  reason: 'Continue the increment you planned and verify the result.',
                  label: 'Open ticket',
                  destination: { section: 'board', focusId: firstTicket.id },
                }
              : candidate
                ? {
                    title: 'Review delivery readiness',
                    reason:
                      'Check the current candidate against the agreed scope, operational evidence, and handoff.',
                    label: 'Check readiness',
                    destination: { section: 'assessment' },
                  }
                : {
                    title: 'Plan your first increment',
                    reason:
                      'Create a useful, testable piece of the agreed scope. You choose the ticket breakdown.',
                    label: 'Open your board',
                    destination: { section: 'board' },
                  };
}

function legacyNextStep(
  snapshot: FieldSnapshot,
  unreadByActor: Partial<Record<FieldActorId, number>>,
): FieldNextStep {
  const work = fieldToday(snapshot);
  const firstTicket =
    work.activeTickets.find((ticket) => ticket.status === 'in_progress') ?? work.activeTickets[0];
  const firstMessage = snapshot.actors.find((actor) => unreadByActor[actor.id]);
  const review = work.reviewRequests[0];
  const incident = work.openIncidents[0];
  return snapshot.engagement.status !== 'active'
    ? {
        title: 'Review your engagement',
        reason: 'Your saved work and assessment remain available.',
        label: 'View assessment',
        destination: { section: 'assessment' as const },
      }
    : incident
      ? {
          title: incident.title,
          reason: 'An incident needs attention.',
          label: 'Review incident',
          destination: { section: 'operations' as const, focusId: incident.id },
        }
      : review
        ? {
            title: review.title,
            reason: 'A reviewer has requested changes on the current revision.',
            label: 'Open pull request',
            destination: { section: 'reviews' as const, focusId: review.id },
          }
        : firstMessage
          ? {
              title: `Catch up with ${firstMessage.name.split(' ')[0]}`,
              reason: `${unreadByActor[firstMessage.id]} unread ${unreadByActor[firstMessage.id] === 1 ? 'message' : 'messages'} in your conversation.`,
              label: 'Read conversation',
              destination: { section: 'inbox' as const, actorId: firstMessage.id },
            }
          : firstTicket
            ? {
                title: firstTicket.title,
                reason:
                  firstTicket.status === 'in_progress'
                    ? 'Continue the ticket you are working on.'
                    : 'Start with this ticket from the current work plan.',
                label: 'View ticket',
                destination: { section: 'board' as const, focusId: firstTicket.id },
              }
            : snapshot.engagement.day === 10
              ? {
                  title: 'Check your evidence',
                  reason:
                    'Review the requirements for your final assessment and address anything still outstanding.',
                  label: 'View assessment',
                  destination: { section: 'assessment' as const },
                }
              : {
                  title: 'Get your bearings',
                  reason: 'Talk with Jordan about the project scope and what to tackle first.',
                  label: 'Talk to Jordan',
                  destination: { section: 'inbox' as const, actorId: 'jordan' as const },
                };
}
